import React, { useEffect } from 'react';
import css from './PageWrapper.module.css';
import '../../css/global.css';
import { Helmet } from 'react-helmet-async';
import VivekaLogo from '../../../assets/meta/viveka-world.png';
import { withRouter } from 'react-router-dom';
import { config } from '../../../index';
import { requestSocket } from '../../../custom-ducks/socket.duck';
import { compose } from 'redux';
import { connect } from 'react-redux';
import SideChat from '../../elements/side-chat/SideChat';
import { addChat, addMessage } from '../../../custom-ducks/socketChat.duck';
import { setCheckBookingInterval } from '../../../custom-ducks/utility.duck';
import { fetchCurrentUserNotifications } from '../../../ducks/user.duck';
import StepsInViveka from '../../elements/steps-in-viveka/StepsInViveka';
import VideoCallDrawer from '../../elements/video-call-drawer/VideoCallDrawer';

function PageWrapper(props) {
  let {
    children,
    title,
    description,
    image,
    url,
    keywords = [],
    schema,
    onConnectSession,
    currentUser,
    chats,
    onAddMessage,
    onAddChat,
    onSetCheckBookingInterval,
    checkBookingInterval,
    onFetchUserNotifications,
    topOffset,
  } = props;

  const setEvents = socketCon => {
    if (!socketCon._callbacks['$exchange-message']) {
      socketCon.on('exchange-message', async (id, message, displayName, dateTime) => {
        const selectedUser = chats.filter(x => x.userId === id);
        if (!selectedUser) {
          onAddMessage({ userId: id, text: message, isFromMe: false, dateTime });
        } else {
          await onAddChat({ name: displayName, userId: id, isOpened: true, messages: [] });
          onAddMessage({ userId: id, text: message, isFromMe: false, dateTime });
        }
      });
    }
  };
  const isProvider = currentUser && currentUser.attributes.profile.publicData.isProvider;
  const setupSocket = async () => {
    if (currentUser) {
      const socketRes = await onConnectSession();
      setEvents(socketRes);
    }
  };

  const setupCheckBookingInterval = async () => {
    if (!checkBookingInterval && isProvider) {
      const interval = setInterval(() => {
        onFetchUserNotifications();
      }, 50000);
      onSetCheckBookingInterval(interval);
    }
  };

  useEffect(() => {
    setupSocket();
  }, [isProvider]);

  const pathname = props.history.location.pathname;
  const isCoachPath =
    (pathname.startsWith('/v-coach/') && !pathname.startsWith('/v-coach/client/')) ||
    pathname.startsWith('/account') ||
    pathname.startsWith('/profile-settings') ||
    pathname.startsWith('/inbox') ||
    pathname.startsWith('/sale') ||
    pathname === '/s' ||
    pathname.startsWith('/s?') ||
    pathname.startsWith('/s/') ||
    pathname.startsWith('/l/') ||
    pathname === '/listings' ||
    (pathname.startsWith('/saas-chats') && !pathname.startsWith('/saas-chats/client'));
  const isCoachSection = isCoachPath && isProvider !== false;

  // Apply body class + favicon synchronously during render so the
  // FIRST paint already has the right theme tokens AND tab icon.
  if (typeof document !== 'undefined') {
    if (isCoachSection) {
      if (!document.body.classList.contains('v-coach-theme')) {
        document.body.classList.add('v-coach-theme');
      }
    } else {
      if (document.body.classList.contains('v-coach-theme')) {
        document.body.classList.remove('v-coach-theme');
      }
    }

    // Favicon swap. V-Coach context → purple V. Default → green V.
    // V-Corp (blue) lives on the vcorp.viveka.world domain and is
    // handled in the viveka-core repo, not here.
    const FAVICON_GREEN = '/static/icons/viveka_fav.png';
    const FAVICON_PURPLE = '/static/icons/viveka-fav-purple.png';
    const targetFavicon = isCoachSection ? FAVICON_PURPLE : FAVICON_GREEN;
    const links = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
    links.forEach(link => {
      // Skip sized variants (16x16, 32x32, 96x96) — those stay as
      // declared in index.html. Only update the unsized fallback
      // which is what most browsers show in the tab.
      const sizes = link.getAttribute('sizes');
      if (sizes && sizes !== 'any') return;
      if (link.getAttribute('href') !== targetFavicon) {
        link.setAttribute('href', targetFavicon);
      }
    });
  }
  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('v-coach-theme');
      }
    };
  }, []);

  useEffect(() => {
    setupCheckBookingInterval();
  }, []);

  url = url ? url : `${config.canonicalRootURL}${props.history.location.pathname}`;
  title = title ? title : "Viveka | World's Largest Coaching Marketplace";
  description = description
    ? description
    : 'Find, book and work with world-class coaches, mentors and speakers on Viveka — the marketplace for professional development and personal growth.';
  image = image ? image : `${config.canonicalRootURL}${VivekaLogo}`;

  const schemaFromProps = Array.isArray(schema) ? schema : [schema];

  const schemaArrayJSONString = JSON.stringify([
    ...schemaFromProps,
    {
      '@context': 'http://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Viveka',
      image: 'https://viveka.world/static/media/viveka-logo.3fc43153.png',
      url: 'https://viveka.world/about',
      author: { '@type': 'Person', name: 'Viveka' },
      datePublished: 'Please insert valid ISO 8601 date/time here. Examples: 2015-07-27 or 2015-07-27T15:30',
      publisher: {
        '@type': 'Organization',
        name: 'We are in the business of Reimaging Humanity by enabling companies and individuals to reach their highest potential.',
      },
    },
  ]);

  return (
    <div className={css.flex}>
      <div id="mainWrapper" className={css.wrapper}>
        <Helmet htmlAttributes={{ lang: 'en' }}>
          <title>{title}</title>
          <link rel="canonical" href={url} />
          <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
          <meta httpEquiv="Content-Language" content="en" />
          <meta name="title" content={title} />
          <meta name="description" content={description} />
          {keywords.length > 0 && (
            <meta name="keywords" content={keywords.join(', ')} data-react-helmet={true} />
          )}
          <meta itemProp="name" content={title} />
          <meta itemProp="description" content={description} />
          <meta itemProp="image" content={image} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={url} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={image} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content={url} />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={image} />
          <script type="application/ld+json">
            {schemaArrayJSONString.replace(/</g, '\\u003c')}
          </script>
        </Helmet>
        {children}
        <SideChat />
        <StepsInViveka topOffset={topOffset} />
      </div>
    </div>
  );
}

const mapStateToProps = state => {
  const { socket } = state.socket;
  const { chats } = state.socketChat;
  const { currentUser } = state.user;
  const { checkBookingInterval } = state.utility;
  return { socket, currentUser, chats, checkBookingInterval };
};

const mapDispatchToProps = dispatch => ({
  onConnectSession: () => dispatch(requestSocket()),
  onAddMessage: data => dispatch(addMessage(data)),
  onAddChat: data => dispatch(addChat(data)),
  onSetCheckBookingInterval: interval => dispatch(setCheckBookingInterval(interval)),
  onFetchUserNotifications: () => dispatch(fetchCurrentUserNotifications()),
});

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(PageWrapper);
